import { useUser } from "@clerk/expo";
import { Copy, Globe, Plus, Trash2 } from "lucide-react-native";
import { FC, useCallback, useEffect, useState } from "react";
import { ScrollView } from "react-native";

import {
  Box,
  DeleteDialog,
  ErrorWithRetry,
  HStack,
  Pressable,
  Skeleton,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization, useStudyContext } from "@/hooks";
import API from "@/hooks/api";
import { Group as GroupType } from "@/types/resources";
import { GroupSize, ResourceType } from "@/types/utils";

import GroupEditor from "./GroupEditor";

/* ── Helpers ─────────────────────────────────────── */

const getGroupCountries = (group: GroupType) => {
  const cs = group.demographics.find((d) => d.demographic === "country");
  return cs
    ? cs.distribution.map((d) => ({
        label: d.label,
        value: d.value,
      }))
    : [];
};

/* ── Props ───────────────────────────────────────── */

interface GroupsProps {
  disabled?: boolean;
  onContinue: () => void;
  onTotalSizeChange?: (size: number) => void;
  resourceId: string;
  resourceType: ResourceType;
}

/* ── Skeleton ────────────────────────────────────── */

export const GroupsSkeleton: FC = () => (
  /* Mirrors the loaded layout: tabs row at top, then the
     editor/summary HStack with `gap-2` and a 12px right inset on
     the left column so toggling between loading and ready states
     doesn't shift columns horizontally. */
  <VStack>
    <HStack className="gap-2">
      <Skeleton className="h-[50px] w-40 rounded-xl" />
      <Skeleton className="h-[50px] w-40 rounded-xl" />
    </HStack>
    <Box className="h-5 mt-2 mb-4" />
    <HStack className="gap-2 flex-col lg:flex-row">
      <VStack className="w-full lg:flex-[2] gap-6 lg:pr-3">
        <Skeleton className="h-[300px] w-full rounded-2xl" />
        <Skeleton className="h-[200px] w-full rounded-2xl" />
      </VStack>
      <VStack className="w-full lg:flex-1">
        <Skeleton className="h-[400px] w-full rounded-2xl" />
      </VStack>
    </HStack>
  </VStack>
);

/* ── Component ───────────────────────────────────── */

const MAX_GROUPS = 6;

const Groups: FC<GroupsProps> = ({
  disabled = false,
  onContinue,
  onTotalSizeChange,
  resourceId,
  resourceType,
}) => {
  const { i18n } = useLocalization();
  const { studyType } = useStudyContext();
  const { user } = useUser();
  const isAdmin = user?.publicMetadata?.admin === true;
  const defaultGroupSize: GroupSize = studyType === "quantitative" ? 100 : 10;

  const [activeGroupIndex, setActiveGroupIndex] = useState(0);
  const [deletingGroup, setDeletingGroup] = useState<GroupType | null>(null);
  const [groups, setGroups] = useState<GroupType[]>([]);

  /* ── API hooks ── */

  const studyGroupsHook = API.Studies.useGetGroups(resourceId);
  const adhocGroupsHook = API.Adhoc.useGetGroups(resourceId);
  const { error, fetchGroups, loading } =
    resourceType === "study" ? studyGroupsHook : adhocGroupsHook;

  const studyCreateHook = API.Studies.useCreateGroup(resourceId);
  const adhocCreateHook = API.Adhoc.useCreateGroup(resourceId);
  const {
    createGroup,
    error: createError,
    loading: creating,
  } = resourceType === "study" ? studyCreateHook : adhocCreateHook;

  const studyDeleteHook = API.Studies.useDeleteGroup(resourceId);
  const adhocDeleteHook = API.Adhoc.useDeleteGroup(resourceId);
  const {
    deleteGroup,
    error: deleteError,
    loading: deleting,
  } = resourceType === "study" ? studyDeleteHook : adhocDeleteHook;

  /* ── Fetch ── */

  const loadGroups = useCallback(async () => {
    const fetched = await fetchGroups();
    if (fetched) setGroups(fetched);
  }, [fetchGroups]);

  // Defer the initial fetch to a timer so the fetcher's loading/error reset
  // doesn't run synchronously inside the effect.
  useEffect(() => {
    if (!resourceId) return;
    const timeout = setTimeout(() => {
      void loadGroups();
    }, 0);
    return () => clearTimeout(timeout);
  }, [loadGroups, resourceId]);

  useEffect(() => {
    const size = groups.reduce((sum, g) => sum + g.size, 0);
    onTotalSizeChange?.(size);
  }, [groups, onTotalSizeChange]);

  /* ── Tab actions ── */

  const handleAddGroup = useCallback(async () => {
    const newGroup = await createGroup({
      description: null,
      demographics: [],
      media: [],
      name: null,
      panel_id: null,
      size: defaultGroupSize,
    });
    if (newGroup) {
      setGroups((prev) => [...prev, newGroup]);
      setActiveGroupIndex(groups.length);
    }
  }, [createGroup, defaultGroupSize, groups.length]);

  const handleDuplicate = useCallback(
    async (index: number) => {
      const source = groups[index];
      const newGroup = await createGroup({
        description: source.description,
        demographics: source.demographics,
        media: [],
        name: source.name,
        panel_id: source.panel?.id ?? null,
        size: source.size,
      });
      if (newGroup) {
        setGroups((prev) => [...prev, newGroup]);
        setActiveGroupIndex(groups.length);
      }
    },
    [createGroup, groups]
  );

  const handleDelete = useCallback(
    (index: number) => {
      const group = groups[index];
      if (group.id) setDeletingGroup(group);
    },
    [groups]
  );

  const handleGroupDeleted = useCallback(async () => {
    if (!deletingGroup) return;
    const success = await deleteGroup(deletingGroup.id);
    if (success) {
      const newGroups = groups.filter((g) => g.id !== deletingGroup.id);
      setGroups(newGroups);
      setDeletingGroup(null);
      if (activeGroupIndex >= newGroups.length) {
        setActiveGroupIndex(Math.max(0, newGroups.length - 1));
      }
    }
  }, [activeGroupIndex, deleteGroup, deletingGroup, groups]);

  const handleGroupChange = useCallback((updated: GroupType) => {
    setGroups((prev) => prev.map((g) => (g.id === updated.id ? updated : g)));
  }, []);

  /* ── Derived ── */

  const activeGroup = groups[activeGroupIndex];

  /* ── Loading ── */

  if (loading) return <GroupsSkeleton />;

  if (error) {
    return (
      <ErrorWithRetry error={error} onRetry={loadGroups} retrying={loading} />
    );
  }

  /* ── Render ── */

  return (
    /* Outer column owns the height — only the inner panels/demographics
       column scrolls. Group tabs and the audience summary card sit
       above/beside the scroll viewport so they stay visible without a
       sticky workaround. */
    <VStack className="flex-1 min-h-0">
      {/* ── Market Tabs ── */}
      {!(disabled && groups.length <= 1) && (
        <>
          <ScrollView
            contentContainerClassName="gap-2"
            horizontal
            showsHorizontalScrollIndicator={false}
            /* Pin the tabs row to its content height — without this
                 React Native Web lets the ScrollView grow to fill the
                 flex column, which in turn stretches the dashed
                 "Add Segment" tile (h-full). */
            style={{ flexGrow: 0, flexShrink: 0 }}
          >
            {groups.map((group, index) => {
              const isActive = index === activeGroupIndex;
              const countries = getGroupCountries(group);
              const name =
                group.name ||
                group.panel?.name ||
                i18n.t("audienceBuilder.marketName", { number: index + 1 });
              return (
                <Pressable
                  key={`tab-${group.id}`}
                  onPress={() => setActiveGroupIndex(index)}
                >
                  <HStack
                    className={`items-center gap-2.5 px-3 py-2 rounded-xl ${
                      isActive
                        ? "bg-primary-50 border border-primary-500/20"
                        : "bg-white border border-outline-200"
                    }`}
                  >
                    <Box
                      className={`w-7 h-7 rounded-lg items-center justify-center ${
                        isActive ? "bg-primary-500" : "bg-background-100"
                      }`}
                    >
                      <Globe
                        color={
                          isActive
                            ? "rgb(var(--color-typography-0))"
                            : "rgb(var(--color-typography-400))"
                        }
                        size={14}
                      />
                    </Box>
                    <VStack>
                      <Text
                        className={`text-sm font-medium ${
                          isActive ? "text-primary-600" : "text-typography-900"
                        }`}
                        numberOfLines={1}
                      >
                        {name}
                      </Text>
                      <Text className="text-[11px] text-typography-400">
                        {countries.length === 1
                          ? countries[0].label
                          : i18n.t("audienceBuilder.countryCount", {
                              count: countries.length,
                            })}{" "}
                        ·{" "}
                        {i18n.t("audienceBuilder.respondentsShort", {
                          count: group.size,
                        })}
                      </Text>
                    </VStack>
                    {isActive && !disabled && (
                      <HStack className="items-center gap-0.5 ml-1">
                        <Pressable
                          className="w-5 h-5 rounded items-center justify-center"
                          disabled={creating}
                          onPress={() => handleDuplicate(index)}
                        >
                          <Copy
                            color="rgb(var(--color-typography-400))"
                            size={12}
                          />
                        </Pressable>
                        {groups.length > 1 && (
                          <Pressable
                            className="w-5 h-5 rounded items-center justify-center"
                            onPress={() => handleDelete(index)}
                          >
                            <Trash2
                              color="rgb(var(--color-typography-400))"
                              size={12}
                            />
                          </Pressable>
                        )}
                      </HStack>
                    )}
                  </HStack>
                </Pressable>
              );
            })}
            {!disabled && (isAdmin || groups.length < MAX_GROUPS) && (
              <Pressable
                className="self-stretch"
                disabled={creating}
                onPress={handleAddGroup}
              >
                <HStack className="items-center gap-2 px-3 h-full min-h-[50px] rounded-xl border border-dashed border-outline-200">
                  <Plus color="rgb(var(--color-typography-400))" size={16} />
                  <Text className="text-sm font-medium text-typography-400">
                    {creating
                      ? i18n.t("audienceBuilder.addingMarket")
                      : i18n.t("audienceBuilder.addMarket")}
                  </Text>
                </HStack>
              </Pressable>
            )}
          </ScrollView>

          <Box className="h-5 mt-2 mb-4">
            {createError && (
              <Text className="text-xs text-red-400">{createError}</Text>
            )}
          </Box>
        </>
      )}

      {/* ── GroupEditor for active tab ── */}
      {activeGroup && (
        <GroupEditor
          disabled={disabled}
          group={activeGroup}
          onChange={handleGroupChange}
          onContinue={onContinue}
          resourceId={resourceId}
          resourceType={resourceType}
        />
      )}

      <DeleteDialog
        body={i18n.t("dashboardNavigator.confirmDeleteGroupMessage")}
        error={deleteError}
        isOpen={deletingGroup !== null}
        loading={deleting}
        onCancel={() => setDeletingGroup(null)}
        onDelete={handleGroupDeleted}
        title={i18n.t("dashboardNavigator.confirmDeleteGroup")}
      />
    </VStack>
  );
};

export default Groups;
